import React, {useEffect, useMemo, useState} from 'react';
import {useTabHashPersistence} from "../../../../hooks/useTabHashPersistence";
import {PageWrapper} from "../../../../components/UI/PageWrapper/PageWrapper";
import {CustomCard} from "../../../../components/UI/AntCustom/CustomCard";
import {message} from "antd";
import {useAppDispatch, useAppSelector} from "../../../../store/store";
import {InitialPage} from "../../../../utils/constans/pagination/pagination";
import {CustomResult} from "../../../../components/UI/AntCustom/CustomResult";
import {NoData} from "../../../../components/UI/NoData/NoData";
import {EmptyLegCard} from "./components/EmptyLegCard";
import {EmptyLegCardSkeleton} from "../../../../components/UI/SkeletonLoaders/EmptyLegCardSkeleton";
import {
  clearChangeEmptyLegRespondStatus,
  clearEmptyLegList,
  getEmptyLegList,
  getEmptyLegListCounts,
} from "../../../../store/reducers/emptyLegsReducer/emptyLegsReducer";
import {AddEmptyLegModal} from "./components/AddEmptyLegModal";
import {CustomButton} from "../../../../components/UI/AntCustom/CustomButton";
import {renderSuccessModal} from "../../../../components/UI/StatusModals/renderSuccessModal";
import {defineMessages, useIntl} from "react-intl";
import {CommonFormMessages} from "../../../../intl/commonMessages/CommonFormMessages";
import {useSearchParams} from "react-router-dom";
import isNumeric from "antd/es/_util/isNumeric";
import CloseIcon from "../../../../media/icons/close.svg?react";
import {useIsMobile} from "../../../../hooks/useWindowSize";
import {CustomTabs} from "../../../../components/UI/AntCustom/CustomTabs";
import {EmptyLegsTabKeysEnum} from "./EmptyLegsTabKeysEnum";


const MyEmptyLegsPageMessages = defineMessages({
  active: {
    id: "myEmptyLegsPage.active",
    defaultMessage: "Active",
  },
  archival: {
    id: "myEmptyLegsPage.archival",
    defaultMessage: "Archive",
  },
  title: {
    id: "myEmptyLegsPage.title",
    defaultMessage: "Empty Legs",
  },
  changeStatusSuccess: {
    id: "myEmptyLegsPage.changeStatusSuccess",
    defaultMessage: "Empty Leg respond status has been successfully updated.",
  },
  create: {
    id: "myEmptyLegsPage.create",
    defaultMessage: "Create Empty Leg",
  },
  noEmptyLegsTitle: {
    id: "myEmptyLegsPage.noEmptyLegsTitle",
    defaultMessage: "You don't have any Empty Legs planned yet.",
  },
  addEmptyLeg: {
    id: "myEmptyLegsPage.addEmptyLeg",
    defaultMessage: "Add Empty Leg",
  },
})


export const MyEmptyLegsPage = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const archivedParams = searchParams.get('archived');
  const emptyLegIdFromParams = isNumeric(searchParams.get('emptyLegId')) ? Number(searchParams.get('emptyLegId')) : undefined;
  const [activeTabKey, setActiveTabKeyState] = useTabHashPersistence(
    Object.values(EmptyLegsTabKeysEnum),
    archivedParams ? EmptyLegsTabKeysEnum.ARCHIVE : EmptyLegsTabKeysEnum.ACTIVE
  );
  
  // Wrapper to match Dispatch<SetStateAction<EmptyLegsTabKeysEnum>> type expected by AddEmptyLegModal
  const setActiveTabKey = (value: EmptyLegsTabKeysEnum | ((prev: EmptyLegsTabKeysEnum) => EmptyLegsTabKeysEnum)) => {
    if (typeof value === 'function') {
      setActiveTabKeyState(value(activeTabKey));
    } else {
      setActiveTabKeyState(value);
    }
  };
  const [currentPage, setCurrentPage] = useState<number>(InitialPage);
  const [isAddEmptyLegModalOpen, setIsAddEmptyLegModalOpen] = useState<boolean>(false);
  const {
    items,
    totalCount,
    isLoading,
  } = useAppSelector(state => state.emptyLegs.itemsObject);
  const emptyLegListCounts = useAppSelector(state => state.emptyLegs.emptyLegListCounts);
  const {
    isSuccess: isChangeStatusSuccess,
    isLoading: isChangeStatusLoading,
    errorMessage: changeStatusErrorMessage,
  } = useAppSelector(state => state.emptyLegs.changeEmptyLegRespondStatus);
  const createEmptyLeg = useAppSelector(state => state.emptyLegs.create);
  const archiveEmptyLeg = useAppSelector(state => state.emptyLegs.delete);

  const tabList = useMemo(() => [
    {key: EmptyLegsTabKeysEnum.ACTIVE, label: `${intl.formatMessage(MyEmptyLegsPageMessages.active)} (${emptyLegListCounts.active})`},
    {key: EmptyLegsTabKeysEnum.ARCHIVE, label: `${intl.formatMessage(MyEmptyLegsPageMessages.archival)} (${emptyLegListCounts.archival})`},
  ], [intl, emptyLegListCounts]);

  useEffect(() => {
    // Load counts on mount
    dispatch(getEmptyLegListCounts());

    return () => {
      dispatch(clearEmptyLegList());
      dispatch(clearChangeEmptyLegRespondStatus());
    }
  }, []);

  useEffect(() => {
    setCurrentPage(InitialPage);

    dispatch(getEmptyLegList({
      query: "",
      page: InitialPage,
      isArchival: activeTabKey === EmptyLegsTabKeysEnum.ARCHIVE,
      emptyLegIds: emptyLegIdFromParams ? [emptyLegIdFromParams] : undefined,
    }));

    // Reload counts when tab changes
    dispatch(getEmptyLegListCounts());
  }, [activeTabKey, emptyLegIdFromParams, dispatch])

  useEffect(() => {
    if (!isChangeStatusLoading) {
      if (isChangeStatusSuccess)
        renderSuccessModal(intl.formatMessage(MyEmptyLegsPageMessages.changeStatusSuccess));
      if (changeStatusErrorMessage)
        message.error(changeStatusErrorMessage);
    }
  }, [isChangeStatusSuccess, changeStatusErrorMessage, isChangeStatusLoading])

  // Reload counts after creating Empty Leg
  useEffect(() => {
    if (createEmptyLeg.isSuccess) {
      dispatch(getEmptyLegListCounts());
    }
  }, [createEmptyLeg.isSuccess, dispatch])

  // Reload counts after archiving Empty Leg
  useEffect(() => {
    if (archiveEmptyLeg.isSuccess) {
      dispatch(getEmptyLegListCounts());
    }
  }, [archiveEmptyLeg.isSuccess, dispatch])

  const loadMore = () => {
    dispatch(getEmptyLegList({
      query: "",
      page: currentPage + 1,
      isArchival: activeTabKey === EmptyLegsTabKeysEnum.ARCHIVE
    }));

    setCurrentPage(prev => prev + 1);
  }

  const removeIdFromSearchParams = () => {
    if (searchParams.has('emptyLegId')) {
      searchParams.delete('emptyLegId')
      setSearchParams(searchParams);
    }
  }

  const createButton = (
    <CustomButton
      heightSize={isMobile ? 's' : 'm'}
      type={'primary'}
      onClick={() => setIsAddEmptyLegModalOpen(true)}
    >
      + {intl.formatMessage(MyEmptyLegsPageMessages.create)}
    </CustomButton>
  )

  return (
    <PageWrapper>
      <CustomCard title={intl.formatMessage(MyEmptyLegsPageMessages.title)} width={'max'} isContainerCard={true}
                  onTabChange={(key: string) => {
                    setActiveTabKey(key as EmptyLegsTabKeysEnum)
                  }} extra={createButton} titlePosition={'space-between'}>
        <CustomTabs
          activeKey={activeTabKey}
          onChange={(key: string) => setActiveTabKey(key as EmptyLegsTabKeysEnum)}
          style={isMobile ? {marginBottom: 0} : {}}
          items={tabList.map(t => ({ key: t.key, label: t.label, children: null }))}
        />

        {emptyLegIdFromParams && <CustomButton icon={<CloseIcon />} onClick={removeIdFromSearchParams}>{intl.formatMessage(CommonFormMessages.showAll)}</CustomButton>}

        {isLoading && items.length === 0
          ? (() => {
              const expectedCount = activeTabKey === EmptyLegsTabKeysEnum.ACTIVE 
                ? emptyLegListCounts.active 
                : emptyLegListCounts.archival;
              const skeletonCount = expectedCount !== undefined && expectedCount > 0 ? expectedCount : 1;
              return [...Array(skeletonCount)].map((_, index) => (
                <EmptyLegCardSkeleton key={`skeleton-${index}`} />
              ));
            })()
          : items.length === 0
          ? (activeTabKey === EmptyLegsTabKeysEnum.ACTIVE
              ? <NoData
                  title={intl.formatMessage(MyEmptyLegsPageMessages.noEmptyLegsTitle)}
                  actionButton={{
                    title: `+ ${intl.formatMessage(MyEmptyLegsPageMessages.create)}`,
                    action: () => setIsAddEmptyLegModalOpen(true),
                    heightSize: 's',
                    ghost: true,
                  }}
                />
              : <CustomResult title={intl.formatMessage(CommonFormMessages.noData)}/>
            )
          : items.map((el) => {
            return <EmptyLegCard key={el.id} emptyLeg={el}/>
          })
        }

        {items.length > 0 && totalCount > items.length &&
          <CustomButton
            loading={isLoading}
            type={'primary'}
            onClick={loadMore}
          >
            {intl.formatMessage(CommonFormMessages.showMore)}
          </CustomButton>}
      </CustomCard>

      {isAddEmptyLegModalOpen &&
        <AddEmptyLegModal isModalVisible={isAddEmptyLegModalOpen} setIsModalVisible={setIsAddEmptyLegModalOpen}
                          setActiveTabKey={setActiveTabKey}/>
      }
    </PageWrapper>
  );
};

